import React, { useEffect, useState } from 'react'
import {
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    IconButton,
    makeStyles,
    Theme,
    Tooltip,
    Typography,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import PhoneIcon from '@material-ui/icons/Phone'
import EmailIcon from '@material-ui/icons/Email'
import LanguageIcon from '@material-ui/icons/Language'
import BusinessIcon from '@material-ui/icons/Business'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import { Company, Service } from '../../models'
import {
    axiosInstance,
    currencyFormatter,
    formatPhoneForDisplay,
} from '../../helpers'

interface Props {
    open: boolean
    property: Company
    handleClose: () => void
}

// Kept for backwards compatibility with hot-reload; image section was removed
const DEFAULT_IMAGE = ''

const useStyles = makeStyles((theme: Theme) => ({
    paper: {
        borderRadius: theme.spacing(1.5),
        maxWidth: 1000,
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
    },
    headerTitle: {
        fontSize: '1.125rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    content: {
        padding: theme.spacing(3),
        overflowY: 'auto',
        maxHeight: 'calc(90vh - 80px)',
    },
    topRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(3),
        marginBottom: theme.spacing(3),
        alignItems: 'flex-start',
    },
    contactBlock: {
        flex: 1,
        minWidth: 200,
    },
    propertyName: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(1.5),
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
    },
    contactRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(0.75),
        fontSize: 14,
    },
    contactLink: {
        color: theme.palette.primary.main,
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    aboutCard: {
        width: 280,
        flexShrink: 0,
        padding: theme.spacing(2.5),
        borderRadius: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.grey[50],
    },
    statRow: {
        fontSize: 14,
        marginBottom: theme.spacing(0.5),
        color: theme.palette.text.primary,
    },
    chipsWrap: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(1),
    },
    mapSection: {
        marginTop: theme.spacing(2),
    },
    mapRow: {
        display: 'flex',
        gap: theme.spacing(2),
        marginTop: theme.spacing(1),
        alignItems: 'stretch',
        flexWrap: 'wrap',
    },
    mapWrap: {
        flex: 1,
        minWidth: 280,
        borderRadius: theme.spacing(1),
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
    },
    mapIframe: {
        width: '100%',
        height: 320,
        border: 0,
        display: 'block',
    },
    addressCard: {
        width: 240,
        flexShrink: 0,
        padding: theme.spacing(2.5),
        borderRadius: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.grey[50],
    },
    openMapsLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(1),
        fontSize: 13,
        color: theme.palette.primary.main,
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    loadingBlock: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4),
    },
}))

export const PropertyDetailModal = (props: Props) => {
    const { open, handleClose, property } = props
    const classes = useStyles()

    const [services, setServices] = useState<Service[]>([])
    const [units, setUnits] = useState<number>(0)
    const [totalSpent, setTotalSpent] = useState<number>(0)
    const [loadingStats, setLoadingStats] = useState(true)

    useEffect(() => {
        if (!open || !property.id) return
        setLoadingStats(true)
        axiosInstance
            .post('company/apartment/get_apartment_data/', { apt: property.id })
            .then((res) => {
                setServices(res.data.services || [])
                setUnits(res.data.units ?? 0)
                setTotalSpent(res.data.total_spent ?? 0)
            })
            .finally(() => setLoadingStats(false))
    }, [open, property.id])

    const mapsQuery = encodeURIComponent(
        [property.address, property.city, property.state, property.zip_code]
            .filter(Boolean)
            .join(', '),
    )
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`

    const websiteUrl =
        property.url &&
        (property.url.startsWith('http')
            ? property.url
            : `https://${property.url}`)

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={false}
            PaperProps={{ className: classes.paper }}
        >
            <Box className={classes.header}>
                <Typography className={classes.headerTitle}>
                    {property.name}
                </Typography>
                <Tooltip title="Close">
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        aria-label="Close"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            <DialogContent className={classes.content}>
                <Box className={classes.topRow}>
                    <Box className={classes.contactBlock}>
                        <Typography className={classes.propertyName}>
                            {property.name}
                        </Typography>
                        <Typography className={classes.sectionTitle}>
                            Contact
                        </Typography>
                        {property.property_manager?.phone && (
                            <Box className={classes.contactRow}>
                                <PhoneIcon fontSize="small" color="action" />
                                <a
                                    href={`tel:${property.property_manager.phone}`}
                                    className={classes.contactLink}
                                >
                                    {formatPhoneForDisplay(
                                        property.property_manager.phone,
                                    )}
                                </a>
                            </Box>
                        )}
                        {property.property_manager?.cell_phone &&
                            property.property_manager.cell_phone !==
                                property.property_manager?.phone && (
                                <Box className={classes.contactRow}>
                                    <PhoneIcon
                                        fontSize="small"
                                        color="action"
                                    />
                                    <a
                                        href={`tel:${property.property_manager.cell_phone}`}
                                        className={classes.contactLink}
                                    >
                                        {formatPhoneForDisplay(
                                            property.property_manager
                                                .cell_phone,
                                        )}
                                    </a>
                                </Box>
                            )}
                        {property.property_manager?.email && (
                            <Box className={classes.contactRow}>
                                <EmailIcon fontSize="small" color="action" />
                                <a
                                    href={`mailto:${property.property_manager.email}`}
                                    className={classes.contactLink}
                                >
                                    {property.property_manager.email}
                                </a>
                            </Box>
                        )}
                        {websiteUrl && (
                            <Box className={classes.contactRow}>
                                <LanguageIcon fontSize="small" color="action" />
                                <a
                                    href={websiteUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={classes.contactLink}
                                >
                                    {property.url}
                                    <OpenInNewIcon
                                        fontSize="small"
                                        style={{
                                            marginLeft: 4,
                                            verticalAlign: 'middle',
                                        }}
                                    />
                                </a>
                            </Box>
                        )}
                        {!property.property_manager?.phone &&
                            !property.property_manager?.email &&
                            !websiteUrl && (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    No contact info
                                </Typography>
                            )}
                    </Box>

                    <Box className={classes.aboutCard}>
                        <Typography className={classes.sectionTitle}>
                            About
                        </Typography>
                        {loadingStats ? (
                            <Box className={classes.loadingBlock}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <>
                                <Typography className={classes.statRow}>
                                    Total units: {units}
                                </Typography>
                                <Typography className={classes.statRow}>
                                    Total spent (EZ Turn):{' '}
                                    {currencyFormatter.format(totalSpent)}
                                </Typography>
                                {services.length > 0 ? (
                                    <>
                                        <Typography
                                            className={classes.sectionTitle}
                                            style={{ marginTop: 16 }}
                                        >
                                            Services used
                                        </Typography>
                                        <Box className={classes.chipsWrap}>
                                            {services.map((s) => (
                                                <Chip
                                                    key={s.id}
                                                    label={s.name}
                                                    size="small"
                                                    style={{
                                                        backgroundColor:
                                                            s.color ||
                                                            undefined,
                                                        color: s.color
                                                            ? '#fff'
                                                            : undefined,
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </>
                                ) : (
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        style={{ marginTop: 8 }}
                                    >
                                        No services
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>
                </Box>

                <Divider style={{ margin: '24px 0 16px' }} />

                {/* Location */}
                <Typography className={classes.sectionTitle}>
                    Location
                </Typography>
                <Box className={classes.mapRow}>
                    <Box className={classes.mapWrap}>
                        <iframe
                            title="Property location"
                            className={classes.mapIframe}
                            loading="lazy"
                            src={`https://www.google.com/maps/embed/v1/place?key=${
                                process.env.REACT_APP_GOOGLE_MAPS_API_KEY
                            }&q=${convertAddress(property)}`}
                        />
                    </Box>
                    <Box className={classes.addressCard}>
                        <Box
                            className={classes.contactRow}
                            style={{ marginBottom: 8 }}
                        >
                            <BusinessIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                                {property.address}
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="textSecondary">
                            {property.city}, {property.state}{' '}
                            {property.zip_code}
                        </Typography>
                        <a
                            href={mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={classes.openMapsLink}
                        >
                            Open in Google Maps
                            <OpenInNewIcon fontSize="small" />
                        </a>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

export const convertAddress = (property: Company) => {
    const parts = [
        property.address,
        property.city,
        property.state,
        property.zip_code,
    ].filter(Boolean)
    return parts.map((p) => String(p).replace(/ /g, '_')).join(',')
}
